import type { Consumption } from '@markku/types'
import { groupBy, sortBy, sumBy } from '@markku/utils'
import dayjs from 'dayjs'

// Type for enriched consumption with calculated value
export type EnrichedConsumption = Consumption & { calculatedValue: number }

export type SummaryDataItem = {
  type: 'electricity' | 'water' | 'heating'
  currentValue: number
  currentCost: number
  change: number
  unit: string
}

export function calculateSummaryData(
  consumptions: EnrichedConsumption[],
  previousYearConsumptions: EnrichedConsumption[],
): SummaryDataItem[] {
  const byType = groupBy(consumptions, (c) => c.type)
  const prevByType = groupBy(previousYearConsumptions, (c) => c.type)

  return (['electricity', 'water', 'heating'] as const).map((type) => {
    const current = byType.get(type) || []
    const previous = prevByType.get(type) || []

    const currentValue = sumBy(current, (c) => c.calculatedValue)
    const previousValue = sumBy(previous, (c) => c.calculatedValue)
    const currentCost = sumBy(current, (c) => (c.cost ? parseFloat(c.cost) : 0))

    const change =
      previousValue > 0
        ? ((currentValue - previousValue) / previousValue) * 100
        : 0

    return {
      type,
      currentValue,
      currentCost,
      change,
      unit: current[0]?.unit || '',
    }
  })
}

export type MonthlyDataPoint = {
  month: string
  current: number | null
  previous: number | null
}

export function generateMonthlyChartData(
  consumptions: EnrichedConsumption[],
  previousYearConsumptions: EnrichedConsumption[],
  selectedType: 'electricity' | 'water' | 'heating',
): MonthlyDataPoint[] {
  const currentByMonth = groupBy(
    consumptions.filter((c) => c.type === selectedType),
    (c) => dayjs(c.date).month(),
  )
  const previousByMonth = groupBy(
    previousYearConsumptions.filter((c) => c.type === selectedType),
    (c) => dayjs(c.date).month(),
  )

  return Array.from({ length: 12 }, (_, i) => {
    const currentMonthConsumptions = currentByMonth.get(i) || []
    const previousMonthConsumptions = previousByMonth.get(i) || []

    const currentValue = sumBy(
      currentMonthConsumptions,
      (c) => c.calculatedValue,
    )
    const previousValue = sumBy(
      previousMonthConsumptions,
      (c) => c.calculatedValue,
    )

    return {
      month: dayjs().month(i).format('MMM'),
      current: currentValue || null,
      previous: previousValue || null,
    }
  })
}

export function generateAvailableYears(): Array<{
  value: string
  label: string
}> {
  const currentYear = new Date().getFullYear()
  return Array.from({ length: 5 }, (_, i) => ({
    value: String(currentYear - i),
    label: String(currentYear - i),
  }))
}

export function sortConsumptionsByDate(
  consumptions: Consumption[],
): Consumption[] {
  return sortBy(consumptions, [(c) => new Date(c.date).getTime(), 'desc'])
}
